import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaKey, FaExchangeAlt, FaSync, FaTools } from 'react-icons/fa';
import { ServiceCard, type Service } from '@/components/ui/ServiceCard';
import { resolveIcon } from '@/sanity/iconMap';
import type { ServicesSectionData } from '@/sanity/types';

// R2 - Icon style for consistency
const iconClass = "h-8 w-8";

// R2 - Services data structure with new INBOCA services
const SERVICES: Service[] = [
  {
    title: 'Alquiler',
    description: 'Entregamos tu módulo por una tarifa mensual con todo tipo de servicios de mantenimiento y equipo especializado.',
    icon: <FaKey className={iconClass} />,
  },
  {
    title: 'Compra y venta',
    description: [
      'Vendemos al cliente el producto que desee.',
      'Compramos tu producto viejo sin dolores de cabeza.',
    ],
    icon: <FaExchangeAlt className={iconClass} />,
  },
  {
    title: 'Intercambio',
    description: 'Según las exigencias del cliente, para que no pierda ni un segundo de uso, se le entrega un módulo totalmente nuevo y a su medida entregando el suyo viejo.',
    icon: <FaSync className={iconClass} />,
  },
  {
    title: 'Reacondicionamiento',
    description: 'Tienes una caseta que no sabes qué hacer y te hace falta: en nuestro taller especializado te la arreglamos y la hacemos a tu medida.',
    icon: <FaTools className={iconClass} />,
  },
];

interface ServicesSectionProps {
  data?: ServicesSectionData | undefined;
}

/**
 * R2 - Services section showcasing INBOCA capabilities
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function ServicesSection({ data }: ServicesSectionProps) {
  const sectionTitle = data?.title ?? 'Qué Hacemos';
  const sectionDesc = data?.description ?? 'Soluciones integrales en módulos prefabricados adaptadas a tus necesidades';

  // R4 - Resolve Sanity items to Service[] or use hardcoded defaults
  const resolvedServices: Service[] = data?.items?.length
    ? data.items.map(item => ({
        title: item.title,
        description: item.description.length === 1 ? item.description[0]! : item.description,
        icon: resolveIcon(item.iconName),
      }))
    : SERVICES;
  const [activeCard, setActiveCard] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setActiveCard(activeCard === index ? null : index);
  };

  return (
    <section className="relative py-20 sm:py-32 bg-inboca-gray">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 sm:mb-20"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            {sectionTitle}
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            {sectionDesc}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {resolvedServices.map((service, index) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={index}
              isActive={activeCard === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
